/** Plan §2.3/§4.3/§6.5 — serialisable snapshot of the solved AMR grid for the
 *  `ConstraintCellOverlay`: flat per-leaf [minX, minY, maxX, maxY, type, level, neumannFaces]
 *  ×7 doubles, NO per-cell residuals (`finalResidual` rides `PreparedTerrainDataV3`). */

import {CellType} from "./AMRGrid.js";
import type {AMRGrid} from "./AMRGrid.js";
import {collectLeaves} from "./AMRGrid.js";

/** Per-leaf record stored in the snapshot. Seven doubles per leaf. */
export const SNAPSHOT_DOUBLES_PER_LEAF = 7;

/** A serialisable constraint-cell snapshot for one grid (macro or working). */
export interface CellSnapshot {
	/** Flat per-leaf packing: [minX, minY, maxX, maxY, type, level, neumannFaces, ...].
	 *  Length = `leavesCount * SNAPSHOT_DOUBLES_PER_LEAF`. */
	cells: Float64Array;
	/** Number of leaves captured. */
	leavesCount: number;
	/** World bbox covered (matches the grid's root AABB). */
	worldMinX: number; worldMinY: number;
	worldMaxX: number; worldMaxY: number;
	/** Solver L² final residual (already on `macroSolveStats`/`workingSolveStats`, copied
	 *  here for one-shot overlay access). */
	finalResidual: number;
}

/** Build a snapshot of `grid`'s leaves. Residual is supplied by the caller — recomputing a
 *  per-cell residual here would inflate the snapshot and the overlay doesn't need it (plan
 *  §2.3 "solver-convergence residual" = the grid-level residual). */
export function buildCellSnapshot(
	grid: AMRGrid,
	finalResidual: number,
): CellSnapshot {
	const leaves = collectLeaves(grid);
	const cells = new Float64Array(leaves.length * SNAPSHOT_DOUBLES_PER_LEAF);
	for (let i = 0; i < leaves.length; i++) {
		const nodeIdx = leaves[i];
		const node = grid.nodes[nodeIdx];
		const cellIdx = node.cellIndex;
		const off = i * SNAPSHOT_DOUBLES_PER_LEAF;
		cells[off + 0] = node.minX;
		cells[off + 1] = node.minY;
		cells[off + 2] = node.maxX;
		cells[off + 3] = node.maxY;
		cells[off + 4] = cellIdx >= 0 ? grid.cells.type[cellIdx] : CellType.Free;
		cells[off + 5] = node.level;
		cells[off + 6] = cellIdx >= 0 ? grid.cells.neumannFaces[cellIdx] : 0;
	}
	return {
		cells,
		leavesCount: leaves.length,
		worldMinX: grid.worldMinX, worldMinY: grid.worldMinY,
		worldMaxX: grid.worldMaxX, worldMaxY: grid.worldMaxY,
		finalResidual,
	};
}

/** Decode a leaf record from a snapshot. Pure helper for the overlay's draw loop. */
export function readLeaf(
	snap: CellSnapshot, i: number,
): {
	minX: number; minY: number; maxX: number; maxY: number;
	type: CellType; level: number; neumannFaces: number;
} {
	const off = i * SNAPSHOT_DOUBLES_PER_LEAF;
	return {
		minX: snap.cells[off + 0],
		minY: snap.cells[off + 1],
		maxX: snap.cells[off + 2],
		maxY: snap.cells[off + 3],
		type: snap.cells[off + 4] as CellType,
		level: snap.cells[off + 5],
		neumannFaces: snap.cells[off + 6],
	};
}